<script setup lang="ts">
import { getFailedPartIcon } from '@/components/asicw/common/utils'
import { useClaims } from '@/composables/asicw/claims'
import type { IClaimContainer, IClaimForm, IFailedParts } from '@/types/asicw/claims'
import { ClaimFailurePart, ClaimFailurePart as ClaimFailurePartEnum } from '@/types/asicw/claims'
import { ContainerType, ContainerTypeSelectOptions } from '@/types/rc/containers'
import { Plus } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import type { UploadFile } from 'element-plus'
import { ElMessage } from 'element-plus'
import { vMaska } from 'maska/vue'
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { DateTime } from 'luxon'

// Generic failure reasons - to be moved to config/endpoint later
const FAILURE_REASONS = [
  'No Power',
  'Overheating',
  'Physical Damage',
  'Not Detected',
  'Malfunction',
  'Short Circuit',
  'Broken Connection',
  'Performance Issues',
  'Unusual Noise',
  'Other',
]
const defaultContainerDimensions = {
  type: ContainerType.Box,
  length: 0,
  width: 0,
  height: 0,
  weight: 0,
}

interface Props {
  claimNumber?: string
}

const props = defineProps<Props>()
const dialogVisible = defineModel<boolean>()

const formRef = ref()
const isSubmitting = ref(false)

// Add new refs for image handling
const imageFileList = ref<UploadFile[]>([])
const imageDialogUrl = ref('')
const imagePreviewDialogVisible = ref(false)
const imageUrlCache = new Map<string, string>()

// Add new refs for log file handling
const logFileList = ref<UploadFile[]>([])

// Get claim details
const claimNumberRef = computed(() => props.claimNumber)
const { getClaimDetailsQuery, updateClaimMutation } = useClaims()
const { data: claimDetails, isLoading, isError, refetch } = getClaimDetailsQuery(claimNumberRef)

// Initialize form with claim data when available
const form = reactive<IClaimForm>({
  failures: [],
  date_of_loss: '',
  notes: '',
  newImages: [],
  deletedPictureIds: [],
  newLogFiles: [],
  deletedLogFileIds: [],
  failed_parts: Object.values(ClaimFailurePartEnum).reduce(
    (acc, part) => {
      acc[part] = { enabled: false, reason: '', part_sn: undefined, part_model: undefined, part_number: undefined }
      return acc
    },
    {} as Record<
      ClaimFailurePart,
      { enabled: boolean; reason: string; part_sn?: string; part_model?: string; part_number?: number }
    >
  ),
  container: undefined as IClaimContainer | undefined,
  created_at: undefined,
})

// Watch for changes in claim details to update form
watch(
  claimDetails,
  (newDetails) => {
    if (newDetails) {
      // Initialize failed_parts with existing failures
      Object.values(ClaimFailurePartEnum).forEach((part) => {
        const existingFailure = newDetails.failures.find((f) => f.part === part)
        form.failed_parts[part] = {
          enabled: !!existingFailure,
          reason: existingFailure?.reason || '',
          part_sn: existingFailure?.part_sn || undefined,
          part_model: existingFailure?.part_model || undefined,
          part_number: existingFailure?.part_number || undefined,
        }
      })
      form.notes = newDetails.notes || ''
      form.created_at = DateTime.fromISO(newDetails.created_at).toISO()!
      form.date_of_loss = DateTime.fromISO(newDetails.date_of_loss).toISO()!

      // Initialize file list with existing images
      if (newDetails.pictures?.length) {
        imageFileList.value = newDetails.pictures.map((pic) => ({
          name: pic.id.toString(),
          url: pic.picture_url,
          status: 'success',
          uid: pic.id,
        }))
      }

      // Initialize file list with existing log files
      if (newDetails.log_files?.length) {
        logFileList.value = newDetails.log_files.map((logFile) => ({
          name: logFile.name,
          url: logFile.file_url,
          status: 'success',
          uid: logFile.id,
        }))
      }

      // Initialize package dimensions
      if (newDetails.container_data) {
        form.container = {
          type: newDetails.container_data.type,
          length: newDetails.container_data.length,
          width: newDetails.container_data.width,
          height: newDetails.container_data.height,
          weight: newDetails.container_data.weight,
        }
      }
    }
  },
  { immediate: true }
)

// Add image URL cache cleanup
onBeforeUnmount(() => {
  imageUrlCache.forEach((url) => {
    URL.revokeObjectURL(url)
  })
  imageUrlCache.clear()
})

const failedPartRule = {
  required: true,
  validator: (
    rule: any,
    value: { enabled: boolean; reason: string; part_sn?: string; part_model?: string; part_number?: number },
    callback: (message?: string) => void
  ) => {
    if (!value.enabled) {
      callback()
      return
    }

    if (!value.reason) {
      callback('Please select a reason')
    }

    // Get the part type from the rule path (e.g., "failed_parts.Hashboard" -> "Hashboard")
    const partType = rule?.field?.split('.')[1] as ClaimFailurePart

    // Power Supply and Control Board: part_sn and part_model are mandatory
    if (partType === ClaimFailurePart.PowerSupply || partType === ClaimFailurePart.ControlBoard) {
      if (!value.part_sn?.trim()) {
        callback('Serial Number is required for this part type')
        return
      }
      if (!value.part_model?.trim()) {
        callback('Model is required for this part type')
        return
      }
    }

    // Fan: part_model is mandatory, and either part_sn or part_number is required
    if (partType === ClaimFailurePart.Fan) {
      if (!value.part_sn?.trim() && !value.part_number) {
        callback('Either Serial Number or Number is required for Fan parts')
        return
      }
      if (!value.part_model?.trim()) {
        callback('Model is required for Fan parts')
        return
      }
    }

    callback()
  },
  trigger: ['blur'],
}

const dimensionRules = {
  validator: (rule: unknown, value: any, callback: (message?: string) => void) => {
    if (value.length <= 0 || value.width <= 0 || value.height <= 0 || value.weight <= 0) {
      callback('Some of the dimensions are invalid')
      return
    }
    callback()
  },
  trigger: ['blur', 'change', 'submit'],
}

const formRules = {
  failed_parts: [
    {
      validator: (rule: unknown, value: IFailedParts, callback: (message?: string) => void) => {
        const enabledParts = Object.values(value).filter((part) => part.enabled)
        if (enabledParts.length === 0) {
          callback('Please select at least one failed part')
          return
        }

        callback()
      },
      trigger: ['change', 'blur'],
    },
  ],
}

const handleImageChange = (file: UploadFile, fileList: UploadFile[]) => {
  if (!file.raw) return

  const isImage = file.raw.type === 'image/jpeg' || file.raw.type === 'image/png'
  const fileInMB = file.raw.size / (1024 * 1024)
  const isLt10M = fileInMB < 10

  if (!isImage) {
    ElMessage.error('Upload file must be a JPG or PNG image')
    const index = fileList.findIndex((f) => f.uid === file.uid)
    if (index !== -1) {
      fileList.splice(index, 1)
    }
    return
  }
  if (!isLt10M) {
    ElMessage.error('Image size can not exceed 10MB')
    const index = fileList.findIndex((f) => f.uid === file.uid)
    if (index !== -1) {
      fileList.splice(index, 1)
    }
    return
  }

  form.newImages.push(file.raw)
}

const handleImageRemove = (file: UploadFile) => {
  if (file.raw) {
    // If it's a newly added file, remove it from newImages
    const index = form.newImages.findIndex((f) => f.name === file.name)
    if (index !== -1) {
      form.newImages.splice(index, 1)
    }
  } else {
    // If it's an existing image, add its ID to deletedPictureIds
    const pictureId = parseInt(file.name)
    if (!isNaN(pictureId)) {
      form.deletedPictureIds.push(pictureId)
    }
  }
  // Trigger form validation
  if (formRef.value) {
    formRef.value.validateField('images')
  }
}

const handleLogFileChange = (file: UploadFile, fileList: UploadFile[]) => {
  if (!file.raw) return

  const allowedTypes = [
    'text/plain',
    'application/zip',
    'application/x-zip-compressed',
    'application/vnd.rar',
    'application/x-rar-compressed',
  ]
  const isLogFile = allowedTypes.includes(file.raw.type)
  const fileInMB = file.raw.size / (1024 * 1024)
  const isLt10M = fileInMB < 10

  if (!isLogFile) {
    ElMessage.error('Upload file must be a TXT, ZIP or RAR file')
    const index = fileList.findIndex((f) => f.uid === file.uid)
    if (index !== -1) {
      fileList.splice(index, 1)
    }
    return
  }
  if (!isLt10M) {
    ElMessage.error('File size can not exceed 10MB')
    const index = fileList.findIndex((f) => f.uid === file.uid)
    if (index !== -1) {
      fileList.splice(index, 1)
    }
    return
  }

  form.newLogFiles.push(file.raw)
}

const handleLogFileRemove = (file: UploadFile) => {
  if (file.raw) {
    // If it's a newly added file, remove it from newLogFiles
    const index = form.newLogFiles.findIndex((f) => f.name === file.name)
    if (index !== -1) {
      form.newLogFiles.splice(index, 1)
    }
  } else {
    // If it's an existing log file, add its ID to deletedLogFileIds
    if (!isNaN(file.uid)) {
      form.deletedLogFileIds.push(file.uid)
    }
  }
}

// Handle failed part switch change
const handleFailedPartChange = (partKey: string, isEnabled: boolean) => {
  formRef.value?.validateField(['log_files', 'failed_parts'])

  if (partKey === ClaimFailurePart.Hashboard) {
    if (isEnabled && !form.container) {
      // If Hashboard part is enabled, create default container
      form.container = structuredClone(defaultContainerDimensions)
    } else if (!isEnabled) {
      // If Hashboard part is disabled, remove container
      form.container = undefined
    }
  }

  // Run the part validation when the failed part is disabled
  if (!isEnabled) {
    formRef.value?.validateField(`failed_parts.${partKey}`)
  }
}

const handleLogFileExceed = () => {
  ElMessage.warning('Maximum 5 files allowed')
}

const onClose = () => {
  dialogVisible.value = false
  form.failures = []
  form.notes = ''
  form.newImages = []
  form.deletedPictureIds = []
  form.newLogFiles = []
  form.deletedLogFileIds = []
  form.container = undefined
  form.created_at = undefined
  form.date_of_loss = undefined
  imageFileList.value = []
}

const onSubmit = async () => {
  if (!claimDetails.value) return

  if (!formRef.value) return

  try {
    // Validate form first
    await formRef.value.validate()

    isSubmitting.value = true
    const updateData = {
      failures: Object.entries(form.failed_parts)
        .filter(([, details]) => details.enabled)
        .map(([part, details]) => ({
          part,
          part_sn: details.part_sn,
          part_model: details.part_model,
          part_number: details.part_number,
          reason: details.reason,
        })),
      notes: form.notes || undefined,
      newPictures: form.newImages,
      deletedPictureIds: form.deletedPictureIds,
      newLogFiles: form.newLogFiles,
      deletedLogFileIds: form.deletedLogFileIds,
      container: form.container,
      created_at: form.created_at || undefined,
      date_of_loss: form.date_of_loss || undefined,
    }

    await updateClaimMutation.mutateAsync({
      claimNumber: claimDetails.value.number,
      data: updateData,
    })
    dialogVisible.value = false
  } catch (error) {
    // If it's a validation error, don't close the dialog
    if (error instanceof Error) {
      return
    }
    // Other errors are handled by mutation error handler
  } finally {
    isSubmitting.value = false
  }
}

const handlePictureCardPreview = (uploadFile: UploadFile) => {
  imageDialogUrl.value = uploadFile.url || (uploadFile.raw ? URL.createObjectURL(uploadFile.raw) : '')
  imagePreviewDialogVisible.value = true
}

const canRemoveFailedPart = (part: { enabled: boolean }) => {
  const enabledParts = Object.values(form.failed_parts).filter((p) => p.enabled)
  if (enabledParts.length === 1 && !part.enabled) {
    return true
  }

  if (enabledParts.length > 1) {
    return true
  }

  return false
}
</script>

<template>
  <el-dialog v-model="dialogVisible" title="Edit claim" width="600px" align-center @close="onClose">
    <el-scrollbar max-height="80vh" class="pr-4">
      <template v-if="isLoading">
        <div class="claim-edit__skeleton">
          <el-skeleton :rows="3" animated />
          <el-divider />
          <el-skeleton :rows="2" animated />
          <el-divider />
          <el-skeleton :rows="4" animated />
        </div>
      </template>
      <template v-else-if="isError">
        <el-result
          icon="error"
          title="Failed to load claim"
          sub-title="Please try again or contact support if the problem persists."
        >
          <template #extra>
            <el-button type="primary" @click="() => refetch()">Try Again</el-button>
          </template>
        </el-result>
      </template>
      <template v-else>
        <el-form ref="formRef" :model="form" :rules="formRules" label-position="top">
          <!-- Customer, Miner, Serial Number section -->
          <div class="claim-edit__field-group">
            <el-form-item label="Customer">
              <template #label>
                <el-text class="mx-1" type="info">Customer</el-text>
              </template>
              <span class="mx-1">{{ claimDetails?.customer }}</span>
            </el-form-item>
            <el-form-item label="Policy Number">
              <template #label>
                <el-text class="mx-1" type="info">Policy Number</el-text>
              </template>
              <span class="mx-1">{{ claimDetails?.policy_number }}</span>
            </el-form-item>
            <el-form-item label="Miner">
              <template #label>
                <el-text class="mx-1" type="info">Model Family</el-text>
              </template>
              <span class="mx-1">{{ claimDetails?.miner.model_family }}</span>
            </el-form-item>
            <el-form-item label="SN">
              <template #label>
                <el-text class="mx-1" type="info">SN</el-text>
              </template>
              <span class="mx-1">{{ claimDetails?.miner.sn }}</span>
            </el-form-item>
          </div>

          <!-- Failures section -->
          <el-form-item label="Failed parts" prop="failed_parts" required>
            <el-form-item
              v-for="(part, key) in form.failed_parts"
              :key="key"
              :prop="`failed_parts.${key}`"
              :rules="failedPartRule"
              class="claim-edit__failed-parts-item"
            >
              <div class="claim-edit__failed-parts-group">
                <div class="claim-edit__failed-parts-header">
                  <el-icon size="20px">
                    <icon :icon="getFailedPartIcon(key)" />
                  </el-icon>
                  <span class="claim-edit__failed-parts-label">{{ key }}</span>
                  <el-switch
                    v-model="part.enabled"
                    style="margin-left: auto"
                    :disabled="!canRemoveFailedPart(part)"
                    @change="(value: any) => handleFailedPartChange(key as string, value as boolean)"
                  />
                </div>
                <el-collapse-transition>
                  <div v-if="part.enabled" class="claim-edit__failed-parts-fields">
                    <div class="claim-edit__field-with-label">
                      <label class="claim-edit__field-label">Reason</label>
                      <el-select
                        v-model="part.reason"
                        placeholder="Select reason"
                        class="claim-edit__failed-parts-select"
                        filterable
                        allow-create
                        @change="
                          () => {
                            formRef?.validateField(['failed_parts', `failed_parts.${key}`])
                          }
                        "
                      >
                        <el-option v-for="reason in FAILURE_REASONS" :key="reason" :label="reason" :value="reason" />
                      </el-select>
                    </div>

                    <div class="claim-edit__field-with-label">
                      <label class="claim-edit__field-label">Serial Number</label>
                      <el-input
                        v-model="part.part_sn"
                        placeholder="Enter serial number"
                        clearable
                        class="claim-edit__failed-parts-input"
                        @blur="formRef?.validateField(`failed_parts.${key}`)"
                        @input="formRef?.validateField(`failed_parts.${key}`)"
                      />
                    </div>

                    <div class="claim-edit__field-with-label">
                      <label class="claim-edit__field-label">Model</label>
                      <el-input
                        v-model="part.part_model"
                        placeholder="Enter model"
                        clearable
                        class="claim-edit__failed-parts-input"
                        @blur="formRef?.validateField(`failed_parts.${key}`)"
                        @input="formRef?.validateField(`failed_parts.${key}`)"
                      />
                    </div>

                    <div v-if="key === ClaimFailurePart.Fan" class="claim-edit__field-with-label">
                      <label class="claim-edit__field-label">Number</label>
                      <el-input-number
                        v-model="part.part_number"
                        clearable
                        placeholder="Enter number"
                        class="claim-edit__failed-parts-input"
                        :min="1"
                        :max="4"
                        :precision="0"
                        controls-position="right"
                        @blur="formRef?.validateField(`failed_parts.${key}`)"
                        @change="formRef?.validateField(`failed_parts.${key}`)"
                      />
                    </div>
                  </div>
                </el-collapse-transition>
              </div>
            </el-form-item>
          </el-form-item>

          <!-- Images section -->
          <el-form-item label="Images">
            <el-upload
              v-model:file-list="imageFileList"
              list-type="picture-card"
              :auto-upload="false"
              :on-change="handleImageChange"
              :on-remove="handleImageRemove"
              :on-preview="handlePictureCardPreview"
              multiple
              accept="image/jpeg,image/png"
            >
              <el-icon><Plus /></el-icon>
              <template #tip>
                <div class="el-upload__tip">JPG, JPEG or PNG files with a size less than 10MB</div>
              </template>
            </el-upload>
            <el-image-viewer
              v-if="imagePreviewDialogVisible"
              :url-list="[imageDialogUrl]"
              @close="imagePreviewDialogVisible = false"
            />
          </el-form-item>

          <!-- Log Files section -->
          <el-form-item label="Log Files">
            <el-upload
              v-model:file-list="logFileList"
              list-type="text"
              :auto-upload="false"
              :on-change="handleLogFileChange"
              :on-remove="handleLogFileRemove"
              :on-exceed="handleLogFileExceed"
              multiple
              :limit="5"
              accept="text/plain,application/zip,application/x-zip-compressed,application/vnd.rar,application/x-rar-compressed"
            >
              <el-button>Upload</el-button>
              <template #tip>
                <div class="el-upload__tip">TXT, ZIP or RAR files with a size less than 10MB</div>
              </template>
            </el-upload>
          </el-form-item>

          <!-- Package dimensions section -->
          <el-form-item v-if="form.container" prop="container" :rules="dimensionRules">
            <template #label>
              <div class="flex flex-row gap-1 items-center">
                <span>Package Dimensions</span>
                <el-tooltip content="How are you planning to pack your miner?" placement="right" effect="light">
                  <Icon icon="material-symbols:help-outline" height="16" />>
                </el-tooltip>
              </div>
            </template>
            <div class="flex flex-col gap-3">
              <el-form-item prop="type" label="Type" class="dimensions-label">
                <el-select v-model="form.container.type">
                  <el-option
                    v-for="option in ContainerTypeSelectOptions"
                    :key="option.key"
                    :label="option.value"
                    :value="option.value"
                  />
                </el-select>
              </el-form-item>
              <div class="flex gap-4">
                <el-form-item label="Length (in)" prop="length" class="dimensions-label">
                  <el-input
                    v-model="form.container.length"
                    v-maska
                    data-maska="9999.9"
                    data-maska-tokens="9:\d:optional"
                    placeholder="0.0"
                    class="w-20"
                    @input="formRef?.validateField('container')"
                  />
                </el-form-item>
                <el-form-item label="Width (in)" prop="width" class="dimensions-label">
                  <el-input
                    v-model="form.container.width"
                    v-maska
                    data-maska="9999.9"
                    data-maska-tokens="9:\d:optional"
                    placeholder="0.0"
                    class="w-20"
                    @input="formRef?.validateField('container')"
                  />
                </el-form-item>
                <el-form-item label="Height (in)" prop="height" class="dimensions-label">
                  <el-input
                    v-model="form.container.height"
                    v-maska
                    data-maska="9999.9"
                    data-maska-tokens="9:\d:optional"
                    placeholder="0.0"
                    class="w-20"
                    @input="formRef?.validateField('container')"
                  />
                </el-form-item>
                <el-form-item label="Weight (lb)" prop="weight" class="dimensions-label">
                  <el-input
                    v-model="form.container.weight"
                    v-maska
                    data-maska="9999.9"
                    data-maska-tokens="9:\d:optional"
                    placeholder="0.0"
                    class="w-20"
                    @input="formRef?.validateField('container')"
                  />
                </el-form-item>
              </div>
            </div>
          </el-form-item>

          <!-- Creation Date section -->
          <el-form-item label="Creation Date" prop="created_at">
            <el-date-picker
              v-model="form.created_at"
              type="datetime"
              placeholder="Select claim creation date"
              format="YYYY-MM-DD HH:mm:ss Z"
              value-format="YYYY-MM-DD HH:mm:ss Z"
              style="width: 100%"
            />
          </el-form-item>

          <!-- Date of Loss section -->
          <el-form-item label="Date of Loss" prop="date_of_loss">
            <el-date-picker
              v-model="form.date_of_loss"
              type="datetime"
              placeholder="Select date of loss"
              format="YYYY-MM-DD HH:mm:ss Z"
              value-format="YYYY-MM-DD HH:mm:ss Z"
              style="width: 100%"
              :disabled-date="(time: Date) => time.getTime() > Date.now()"
            />
          </el-form-item>

          <!-- Notes textarea -->
          <el-form-item label="Notes" prop="notes">
            <el-input v-model="form.notes" type="textarea" :rows="4" placeholder="Enter notes about the claim" />
          </el-form-item>
        </el-form>
      </template>
    </el-scrollbar>
    <!-- Footer actions -->
    <template #footer>
      <div class="claim-edit__footer">
        <el-button @click="onClose">Cancel</el-button>
        <el-button type="primary" :loading="isSubmitting" :disabled="isLoading || isError" @click="onSubmit">
          Save changes
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
@use '@/styles/element/overrides';

:deep() {
  @include overrides.hide-image-preview-deletion-tip;
}

.claim-edit {
  // Block: claim-edit
  &__skeleton {
    padding: 20px;
  }

  &__field-group {
    display: flex;
    flex-flow: row wrap;
    gap: 16px;
    margin-bottom: 24px;

    :deep(.el-form-item) {
      margin-bottom: 0;
      width: unset !important;
    }
  }

  &__input-group {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;
  }

  // Failed parts section
  &__failed-parts {
    &-group {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-spacing-md);
      width: 100%;
    }

    &-item {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-spacing-sm);
      margin-bottom: 1rem !important;
    }

    &-header {
      display: flex;
      align-items: center;
      width: 100%;
      gap: var(--spacing-spacing-sm);
    }

    &-label {
      font-size: var(--font-size-body);
      font-weight: 500;
    }

    &-select {
      width: 100%;
    }

    &-fields {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-spacing-3xs);
    }

    &-input {
      width: 100%;
    }
  }

  &__field-with-label {
    display: flex;
    flex-direction: column;
  }

  &__field-label {
    font-size: var(--font-size-sm);
    font-weight: 500;
    color: var(--el-text-color-regular);
  }

  // Images section
  &__image {
    &-list {
      display: flex;
      flex-flow: row wrap;
      gap: 16px;
      align-items: center;
    }

    &-item {
      width: 120px;
      height: 120px;
      border-radius: 4px;
      overflow: hidden;

      :deep(.el-image) {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    &-preview {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
      gap: 8px;
      padding: 8px;
      max-width: 320px;

      &-thumbnail {
        width: 100px;
        height: 100px;
        object-fit: cover;
        border-radius: 4px;
      }
    }
  }

  &__upload {
    &-area {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 120px;
      height: 120px;
      border: 1px dashed var(--el-border-color);
      border-radius: 4px;
      cursor: pointer;
      transition: border-color 0.3s;

      &:hover {
        border-color: var(--el-color-primary);
      }
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  &__failure-inputs {
    display: flex;
    align-items: center;
    gap: var(--spacing-spacing-sm);
    flex-grow: 1;

    :deep(.el-input) {
      flex: 1;
    }
  }
}

.dimensions-label :deep(.el-form-item__label) {
  font-size: var(--font-size-sm);
}

:deep(.el-form-item__label) {
  font-weight: bold;
}
</style>
